import React, { useState, useEffect, useRef, useCallback, createContext, useContext, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import AdminPanel from './AdminPanel';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from './i18n/index.js';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useNovaApp from "@/hooks/useNovaApp";
import logger from "@/utils/logger";
import GiftCardRewardSection from "@/components/GiftCardRewardSection";
import SpotifyWidget from "@/components/SpotifyWidget";
import {
  Home, Settings, MessageCircle, Play, Pause, Music, Watch, BarChart3,
  Send, X, Volume2, VolumeX, Check, Smartphone, Phone, Calendar,
  FileText, Globe, Palette, User, Heart, Footprints, Battery, Zap,
  LogOut, Mail, Lock, UserPlus, Award, Gift, Copy, CheckCircle,
  CreditCard, Crown, Trophy, Star, Flame, Gem, Sparkles,
  ShoppingBag, Coffee, Gamepad2, Tag, ArrowRight, Package, Ticket,
  Users, Clock, Trash2, ToggleLeft, ToggleRight, Plus, Share2
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });
const HTTP_UNAUTHORIZED = 401;

// Add interceptor for token refresh
api.interceptors.response.use(
  res => res,
  async err => {
    if (err.response?.status === HTTP_UNAUTHORIZED && !err.config._retry) {
      err.config._retry = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return api(err.config);
      } catch {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

// ============ AUTH CONTEXT ============
const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (err) {
      logger.error("Auth check failed:", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { logger.error("Logout error:", err); }
    setUser(null);
  };

  const contextValue = useMemo(() => ({ user, setUser, loading, logout, checkAuth }), [user, loading, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }

// ============ HELPER ============
function formatApiError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const APP_ICONS = { messages: Smartphone, phone: Phone, calendar: Calendar, notes: FileText, music: Music, browser: Globe };
const AVATAR_ICONS = { default: "🪨", crystal: "💎", flame: "🔥", cosmic: "✨" };
const NOVA_AVATAR = "https://images.unsplash.com/photo-1755375551130-cf278d391d99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxjdXRlJTIwbWluaW1hbGlzdCUyMHN0b25lJTIwcGViYmxlfGVufDB8fHx8MTc3NTk0NDI0Mnww&ixlib=rb-4.1.0&q=85&w=100";

// ============ AUTH CALLBACK (Google OAuth) ============
function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/login"); return; }

    const sessionId = match[1];
    (async () => {
      try {
        const res = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(res.data);
        navigate("/", { replace: true });
      } catch (err) {
        logger.error("Google OAuth failed:", err);
        navigate("/login");
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="auth-page" data-testid="auth-callback">
      <div className="auth-card"><div className="spinner" style={{ margin: "2rem auto" }} /></div>
    </div>
  );
}

// ============ LOGIN PAGE ============
function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <motion.div className="auth-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-6">
          <img src={NOVA_AVATAR} alt="Nova" className="w-16 h-16 rounded-full mx-auto mb-3 object-cover" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope" }}>Welcome to Nova</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted-foreground)" }}>Your focus assistant</p>
        </div>

        <form onSubmit={handleLogin}>
          {error && <div className="auth-error" data-testid="login-error">{error}</div>}
          <div className="input-group">
            <Mail className="input-icon" />
            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email" />
          </div>
          <div className="input-group">
            <Lock className="input-icon" />
            <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password" />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="login-submit">
            {loading ? <div className="spinner" style={{ margin: "0 auto" }} /> : "Sign In"}
          </button>
        </form>

        <div className="auth-divider"><span>or</span></div>

        <button className="btn-google w-full" onClick={handleGoogleLogin} data-testid="google-login-button">
          <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continue with Google
        </button>

        <p className="auth-footer">
          Don't have an account? <button onClick={() => navigate("/register")} data-testid="go-to-register">Sign up</button>
        </p>
      </motion.div>
    </div>
  );
}

// ============ REGISTER PAGE ============
function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await api.post("/auth/register", { name, email, password });
      setUser(res.data);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="auth-page" data-testid="register-page">
      <motion.div className="auth-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope" }}>Create Account</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted-foreground)" }}>Start your focus journey</p>
        </div>

        <form onSubmit={handleRegister}>
          {error && <div className="auth-error" data-testid="register-error">{error}</div>}
          <div className="input-group">
            <User className="input-icon" />
            <input type="text" placeholder="Name" value={name} onChange={e => setName(e.target.value)} required data-testid="register-name" />
          </div>
          <div className="input-group">
            <Mail className="input-icon" />
            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="register-email" />
          </div>
          <div className="input-group">
            <Lock className="input-icon" />
            <input type="password" placeholder="Password (min 6 chars)" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} data-testid="register-password" />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit">
            {loading ? <div className="spinner" style={{ margin: "0 auto" }} /> : "Create Account"}
          </button>
        </form>

        <div className="auth-divider"><span>or</span></div>

        <button className="btn-google w-full" onClick={handleGoogleLogin} data-testid="google-register-button">
          <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continue with Google
        </button>

        <p className="auth-footer">
          Already have an account? <button onClick={() => navigate("/login")} data-testid="go-to-login">Sign in</button>
        </p>
      </motion.div>
    </div>
  );
}

// ============ PAYMENT SUCCESS ============
function PaymentSuccess() {
  const [status, setStatus] = useState("checking");
  const [plan, setPlan] = useState(null);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const hasPolled = useRef(false);

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { navigate("/"); return; }

    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          setPlan(res.data.plan_id);
          await checkAuth();
          return;
        }
        if (res.data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (err) { logger.error("Payment poll error:", err); }
      attempts++;
      if (attempts < 5) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [navigate, checkAuth]);

  return (
    <div className="auth-page" data-testid="payment-success-page">
      <motion.div className="auth-card text-center" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
        {status === "checking" && (
          <>
            <div className="spinner" style={{ margin: "2rem auto" }} />
            <p>Verifying payment...</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "var(--primary)", color: "white" }}>
              <CheckCircle className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "Manrope" }}>Payment Successful!</h2>
            <p className="mb-4" style={{ color: "var(--muted-foreground)" }}>You've been upgraded to {plan === "family" ? "Family" : "Pro"} plan</p>
            <button className="btn-primary" onClick={() => navigate("/")} data-testid="go-to-home">Go to Nova</button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <h2 className="text-xl font-bold mb-2">Payment Issue</h2>
            <p className="mb-4" style={{ color: "var(--muted-foreground)" }}>There was an issue processing your payment.</p>
            <button className="btn-primary" onClick={() => navigate("/settings")} data-testid="retry-payment">Try Again</button>
          </>
        )}
      </motion.div>
    </div>
  );
}

// ============ MAIN APP ============
function MainApp() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const nova = useNovaApp(api, user, setUser);
  const {
    screen, setScreen, focusActive, time, apps, constants, watchData, isPro,
    showPlans, setShowPlans, showSpotify, setShowSpotify, showStats, setShowStats,
    showAI, setShowAI, showAppEdit, setShowAppEdit, showCustomize, setShowCustomize,
    showAchievements, setShowAchievements, showMarketplace, setShowMarketplace,
    showRedemptionSuccess, setShowRedemptionSuccess, marketplaceData, marketplaceFilter,
    setMarketplaceFilter, redeeming, referralData, referralInput, setReferralInput,
    referralMsg, showReferral, setShowReferral, schedules, showSchedule, setShowSchedule,
    newSchedule, setNewSchedule, chartData, messages, userInput, setUserInput,
    isSending, isSpeaking, ttsEnabled, setTtsEnabled, messagesEndRef, audioRef,
    achievementsData, copiedCoupon,
    allowedApps, allowedAppsCount, filteredMarketplaceItems, formatTime, formatDate,
    getThemeClass, getTextColor,
    updateSettings, updateApps, startFocus, endFocus, toggleApp, handleSend,
    handleCheckout, applyReferral, createSchedule, deleteSchedule,
    toggleScheduleActive, toggleDay, redeemCoupon, copyCoupon,
    fetchAchievements, fetchMarketplace, fetchChartData, fetchSchedules, fetchReferral,
  } = nova;

  return (
    <div className="app-container">
      <audio ref={audioRef} />

      {/* Lock Screen */}
      {screen === "lock" && (
        <div className={`lock-screen ${getThemeClass()}`}>
          <div className="flex-1 flex flex-col justify-center items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`clock-display ${getTextColor()}`} data-testid="clock-display">
              {formatTime(time)}
            </motion.div>
            <div className={`date-display mt-2 ${getTextColor()}`} data-testid="date-display">{formatDate(time)}</div>

            <motion.div className="focus-indicator mt-8" animate={focusActive ? { scale: [1, 1.02, 1] } : {}} transition={{ repeat: Infinity, duration: 2 }} data-testid="focus-indicator">
              <div className={`focus-dot ${focusActive ? "pulse" : "inactive"}`} />
              <span className={getTextColor()}>{focusActive ? "Focus Active" : "Focus Off"}</span>
            </motion.div>

            {isPro && user?.watch_connected && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card mt-6 w-full max-w-xs" data-testid="watch-data-card">
                <div className="watch-data">
                  <div className="watch-stat"><Heart className="w-5 h-5 mx-auto mb-1 text-red-400" /><div className="font-semibold">{watchData.heartRate}</div><div className="text-xs opacity-70">BPM</div></div>
                  <div className="watch-stat"><Footprints className="w-5 h-5 mx-auto mb-1 text-blue-400" /><div className="font-semibold">{watchData.steps.toLocaleString()}</div><div className="text-xs opacity-70">Steps</div></div>
                  <div className="watch-stat"><Battery className="w-5 h-5 mx-auto mb-1 text-green-400" /><div className="font-semibold">{watchData.battery}%</div><div className="text-xs opacity-70">Battery</div></div>
                </div>
              </motion.div>
            )}

            {focusActive && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card mt-6 w-full" data-testid="allowed-apps-card">
                <div className={`text-sm font-medium mb-3 ${getTextColor()}`}>Allowed Apps</div>
                <div className="flex flex-wrap gap-3">
                  {allowedApps.map(a => {
                    const Icon = APP_ICONS[a.id] || Smartphone;
                    return <div key={a.id} className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1.5"><Icon className="w-4 h-4" /><span className="text-sm">{a.name}</span><span className="text-xs opacity-70">{a.minutes_allowed}m</span></div>;
                  })}
                </div>
              </motion.div>
            )}
          </div>

          <motion.button className={`w-full py-4 rounded-2xl font-semibold text-lg ${focusActive ? "bg-white/20 backdrop-blur border border-white/30" : "bg-[var(--primary)] text-white"}`} whileTap={{ scale: 0.98 }} onClick={() => focusActive ? endFocus() : startFocus()} data-testid="focus-toggle-button">
            <div className="flex items-center justify-center gap-2">
              {focusActive ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              <span>{focusActive ? "End Focus" : "Start Focus"}</span>
            </div>
          </motion.button>

          {/* Spotify Widget */}
          <div className="mt-4" style={{ paddingBottom: '8px' }}>
            <SpotifyWidget
              api={api}
              isFocusActive={focusActive}
              focusSessionId={user?.user_id + "_" + Date.now()}
              focusDurationMin={Math.floor(time / 60)}
            />
          </div>
        </div>
      )}

      {/* Settings Screen */}
      {screen === "settings" && (
        <div className="settings-screen">
          <div className="header">
            <h1 className="section-title">Settings</h1>
            <div className="flex items-center gap-2">
              {isPro && <span className="pro-badge">PRO</span>}
              <button onClick={() => { logout(); navigate("/login"); }} className="p-2 hover:bg-[var(--muted)] rounded-full" data-testid="logout-button"><LogOut className="w-5 h-5" /></button>
            </div>
          </div>

          {/* User Info */}
          <div className="clay-card p-4 mb-4" data-testid="user-info">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--primary)] flex items-center justify-center text-white"><User className="w-5 h-5" /></div>
              <div>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>{user?.email}</div>
              </div>
            </div>
          </div>

          {/* Plan */}
          <div className="clay-card p-4 mb-4" data-testid="plan-section">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold">{constants.plans[user?.current_plan]?.name || "Free"} Plan</div>
                <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>{constants.plans[user?.current_plan]?.price || "$0"}</div>
              </div>
              <button className="btn-secondary" onClick={() => setShowPlans(true)} data-testid="change-plan-button">
                {isPro ? "Manage" : <><CreditCard className="w-4 h-4 inline mr-1" />Upgrade</>}
              </button>
            </div>
          </div>

          {/* Trial Expiry Banner */}
          {user?.trial_active && user?.trial_days_left !== undefined && (
            <div className="p-3 rounded-xl mb-4 flex items-center gap-3" style={{ background: user.trial_days_left <= 2 ? "rgba(196,109,94,0.1)" : "rgba(44,76,59,0.08)", border: `1px solid ${user.trial_days_left <= 2 ? "var(--accent)" : "var(--primary)"}` }} data-testid="trial-banner">
              <Clock className="w-5 h-5 flex-shrink-0" style={{ color: user.trial_days_left <= 2 ? "var(--accent)" : "var(--primary)" }} />
              <div className="flex-1">
                <div className="font-semibold text-sm">{user.trial_days_left} day{user.trial_days_left !== 1 ? "s" : ""} left on Pro trial</div>
                <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>{user.trial_days_left <= 2 ? "Subscribe now to keep your Pro features!" : "Enjoying Pro? Subscribe to keep access."}</div>
              </div>
              {user.trial_days_left <= 2 && <button className="btn-accent py-1.5 px-3 text-xs" onClick={() => setShowPlans(true)} data-testid="trial-subscribe-btn">Subscribe</button>}
            </div>
          )}

          {/* Referral */}
          <div className="clay-card p-4 mb-4 cursor-pointer" onClick={() => { fetchReferral(); setShowReferral(true); }} data-testid="referral-section">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Share2 className="w-5 h-5" style={{ color: "var(--primary)" }} />
                <div>
                  <div className="font-semibold">Refer a Friend</div>
                  <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>Both earn a Bronze coupon</div>
                </div>
              </div>
              <Users className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
            </div>
          </div>

          {/* Focus Schedule */}
          <div className="clay-card p-4 mb-4 cursor-pointer" onClick={() => { fetchSchedules(); setShowSchedule(true); }} data-testid="schedule-section">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5" style={{ color: "var(--primary)" }} />
                <div>
                  <div className="font-semibold">Focus Schedule</div>
                  <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>{schedules.length} schedule{schedules.length !== 1 ? "s" : ""} active</div>
                </div>
              </div>
              <Clock className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
            </div>
          </div>

          {/* Achievements */}
          <div className="clay-card p-4 mb-4 cursor-pointer" onClick={() => { fetchAchievements(); setShowAchievements(true); }} data-testid="achievements-section">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5" style={{ color: "var(--accent)" }} />
                <div>
                  <div className="font-semibold">Achievements & Rewards</div>
                  <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>{achievementsData.unlocked.length} unlocked</div>
                </div>
              </div>
              <Award className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
            </div>
          </div>

          {/* AI Mode (Pro) */}
          {isPro && (
            <div className="clay-card p-4 mb-4" data-testid="ai-mode-section">
              <div className="font-semibold mb-3">AI Assistant Mode</div>
              <div className="ai-mode-toggle">
                <button className={`ai-mode-btn ${user?.ai_mode === "rule_based" ? "active" : ""}`} onClick={() => updateSettings({ ai_mode: "rule_based" })} data-testid="ai-mode-rule-based">Rule-Based</button>
                <button className={`ai-mode-btn ${user?.ai_mode === "gpt" ? "active" : ""}`} onClick={() => updateSettings({ ai_mode: "gpt" })} data-testid="ai-mode-gpt">GPT-5.2 <Zap className="inline w-3 h-3 ml-1" /></button>
              </div>
            </div>
          )}

          {/* Language Selector */}
          <LanguageSwitcher />

          {/* Customize (Pro) */}
          {isPro && (
            <div className="clay-card p-4 mb-4 cursor-pointer" onClick={() => setShowCustomize(true)} data-testid="customize-section">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">{AVATAR_ICONS[user?.selected_avatar] || "🪨"}</div>
                  <div><div className="font-semibold">Customize</div><div className="text-sm" style={{ color: "var(--muted-foreground)" }}>Theme & Avatar</div></div>
                </div>
                <Palette className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
              </div>
            </div>
          )}

          {/* Smartwatch (Pro) */}
          {isPro && (
            <div className="clay-card p-4 mb-4" data-testid="smartwatch-section">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Watch className="w-5 h-5" />
                  <div><div className="font-semibold">Smartwatch</div><div className="text-sm" style={{ color: "var(--muted-foreground)" }}>{user?.watch_connected ? "Connected" : "Not connected"}</div></div>
                </div>
                <div className={`toggle-switch ${user?.watch_connected ? "active" : ""}`} onClick={() => updateSettings({ watch_connected: !user?.watch_connected })} data-testid="smartwatch-toggle" />
              </div>
            </div>
          )}

          {/* App Access */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="section-title">App Access</h2>
              {!isPro && <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>{allowedAppsCount}/{constants.plans?.free?.app_limit || 3}</span>}
            </div>
            {apps.map(a => {
              const Icon = APP_ICONS[a.id] || Smartphone;
              return (
                <div key={a.id} className="app-item" data-testid={`app-item-${a.id}`}>
                  <div className="app-info">
                    <div className="app-icon"><Icon className="w-5 h-5" /></div>
                    <div>
                      <div className="font-medium">{a.name}</div>
                      <button className="text-sm hover:text-[var(--primary)]" style={{ color: "var(--muted-foreground)" }} onClick={() => setShowAppEdit(a)} data-testid={`app-edit-${a.id}`}>{a.minutes_allowed} min/day</button>
                    </div>
                  </div>
                  <div className={`toggle-switch ${a.allowed ? "active" : ""}`} onClick={() => toggleApp(a.id)} data-testid={`app-toggle-${a.id}`} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Nav */}
      <nav className="bottom-nav">
        <button className={`nav-btn ${screen === "lock" ? "active" : ""}`} onClick={() => setScreen("lock")} data-testid="nav-home"><Home className="w-6 h-6" /><span className="text-xs">Home</span></button>
        {isPro && (<><button className="nav-btn" onClick={() => setShowSpotify(true)} data-testid="nav-spotify"><Music className="w-6 h-6" /><span className="text-xs">Music</span></button><button className="nav-btn" onClick={() => { fetchChartData(); setShowStats(true); }} data-testid="nav-stats"><BarChart3 className="w-6 h-6" /><span className="text-xs">Stats</span></button></>)}
        <button className="nav-btn" onClick={() => { fetchAchievements(); fetchMarketplace(); setShowMarketplace(true); }} data-testid="nav-achievements"><ShoppingBag className="w-6 h-6" /><span className="text-xs">Shop</span></button>
        <button className={`nav-btn ${screen === "settings" ? "active" : ""}`} onClick={() => setScreen("settings")} data-testid="nav-settings"><Settings className="w-6 h-6" /><span className="text-xs">Settings</span></button>
      </nav>

      {/* AI FAB */}
      <motion.button className="fab" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={() => setShowAI(true)} data-testid="ai-fab"><MessageCircle className="w-6 h-6" /></motion.button>

      {/* Modals */}
      <AnimatePresence>
        {/* Plans Modal */}
        {showPlans && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowPlans(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} data-testid="plans-modal">
              <div className="flex justify-between items-center mb-6"><h2 className="section-title">Choose Plan</h2><button onClick={() => setShowPlans(false)} data-testid="close-plans-modal"><X className="w-6 h-6" /></button></div>

              {!isPro && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl mb-5 relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--primary), #3D6B52)", color: "white" }} data-testid="try-pro-banner">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}><Sparkles className="w-6 h-6" /></div>
                    <div className="flex-1">
                      <div className="font-bold text-base" style={{ fontFamily: "Manrope" }}>Try Pro for Free</div>
                      <div className="text-sm opacity-90">Unlock all features instantly. No credit card needed.</div>
                    </div>
                  </div>
                  <button className="w-full mt-3 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2" style={{ background: "rgba(255,255,255,0.95)", color: "var(--primary)" }} onClick={async () => { try { const r = await api.post("/plans/try-pro"); setUser(r.data); setShowPlans(false); } catch (e) { logger.error("Try pro error:", e); } }} data-testid="try-pro-button">
                    <Crown className="w-4 h-4" /> Activate Pro Free Trial
                  </button>
                </motion.div>
              )}

              {Object.entries(constants.plans).map(([id, plan]) => (
                <div key={id} className={`plan-card ${user?.current_plan === id ? "selected" : ""} ${id === "pro" ? "pro" : ""}`} data-testid={`plan-card-${id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div><div className="font-semibold text-lg">{plan.name}</div><div style={{ color: "var(--accent)" }} className="font-bold">{plan.price}</div></div>
                    {user?.current_plan === id && <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "var(--primary)" }}><Check className="w-4 h-4 text-white" /></div>}
                  </div>
                  <ul className="space-y-2 mb-3">{plan.features?.map((f, i) => <li key={i} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4" style={{ color: "var(--primary)" }} />{f}</li>)}</ul>
                  {id !== "free" && user?.current_plan !== id && (
                    <button className="btn-accent w-full" onClick={() => handleCheckout(id)} data-testid={`checkout-${id}`}>
                      <CreditCard className="w-4 h-4 inline mr-2" />Subscribe to {plan.name}
                    </button>
                  )}
                  {id === "free" && user?.current_plan !== "free" && <button className="btn-secondary w-full" onClick={() => { updateSettings({ current_plan: "free" }); setShowPlans(false); }}>Switch to Free</button>}
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* Spotify Modal */}
        {showSpotify && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowSpotify(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} data-testid="spotify-modal">
              <div className="flex justify-between items-center mb-6"><h2 className="section-title">Focus Playlists</h2><button onClick={() => setShowSpotify(false)} data-testid="close-spotify-modal"><X className="w-6 h-6" /></button></div>
              {constants.playlists.map(pl => (
                <div key={pl.id} className="playlist-item" onClick={() => window.open(`https://open.spotify.com/playlist/${pl.uri.split(":")[2]}`, "_blank")} data-testid={`playlist-${pl.id}`}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: "var(--primary)" }}><Music className="w-6 h-6 text-white" /></div>
                  <div className="flex-1"><div className="font-semibold">{pl.name}</div><div className="text-sm" style={{ color: "var(--muted-foreground)" }}>Spotify Playlist</div></div>
                  <Play className="w-5 h-5" style={{ color: "var(--primary)" }} />
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* Stats Modal */}
        {showStats && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowStats(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} data-testid="stats-modal">
              <div className="flex justify-between items-center mb-6"><h2 className="section-title">Your Stats</h2><button onClick={() => setShowStats(false)} data-testid="close-stats-modal"><X className="w-6 h-6" /></button></div>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="stat-card"><div className="stat-value" style={{ fontSize: "1.75rem" }}>{user?.focus_sessions || 0}</div><div className="stat-label">Sessions</div></div>
                <div className="stat-card"><div className="stat-value" style={{ fontSize: "1.75rem" }}>{user?.focus_minutes || 0}</div><div className="stat-label">Minutes</div></div>
                <div className="stat-card"><div className="stat-value" style={{ fontSize: "1.75rem" }}><Flame className="w-5 h-5 inline" style={{ color: "var(--accent)" }} /> {user?.focus_streak || 0}</div><div className="stat-label">Streak</div></div>
              </div>

              {/* Progress Chart */}
              <div className="p-4 rounded-xl mb-4" style={{ background: "var(--secondary)" }}>
                <div className="font-semibold mb-3 text-sm">Focus Minutes (Last 14 Days)</div>
                <div style={{ width: "100%", height: 180 }} data-testid="focus-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="date" tickFormatter={d => { const dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en", { month: "short", day: "numeric" }); }} tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                      <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                      <Tooltip labelFormatter={d => new Date(d + "T00:00:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" })} contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="minutes" fill="var(--primary)" radius={[4, 4, 0, 0]} name="Minutes" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="p-4 rounded-xl" style={{ background: "var(--secondary)" }}>
                <div className="font-semibold mb-3 text-sm">Sessions Per Day</div>
                <div style={{ width: "100%", height: 140 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="date" tickFormatter={d => { const dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en", { month: "short", day: "numeric" }); }} tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                      <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} allowDecimals={false} />
                      <Tooltip labelFormatter={d => new Date(d + "T00:00:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" })} contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="sessions" fill="var(--accent)" radius={[4, 4, 0, 0]} name="Sessions" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Achievements Modal */}
        {showAchievements && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAchievements(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} data-testid="achievements-modal">
              <div className="flex justify-between items-center mb-4"><h2 className="section-title">Achievements & Rewards</h2><button onClick={() => setShowAchievements(false)} data-testid="close-achievements-modal"><X className="w-6 h-6" /></button></div>

              <div className="flex gap-4 mb-6">
                <div className="flex-1 p-3 rounded-lg text-center" style={{ background: "var(--secondary)" }}>
                  <Flame className="w-5 h-5 mx-auto mb-1" style={{ color: "var(--accent)" }} />
                  <div className="font-bold">{achievementsData.focus_streak}</div>
                  <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>Day Streak</div>
                </div>
                <div className="flex-1 p-3 rounded-lg text-center" style={{ background: "var(--secondary)" }}>
                  <Star className="w-5 h-5 mx-auto mb-1" style={{ color: "var(--accent)" }} />
                  <div className="font-bold">{achievementsData.focus_sessions}</div>
                  <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>Sessions</div>
                </div>
              </div>

              {achievementsData.definitions.map(ach => {
                const unlocked = achievementsData.unlocked.find(u => u.achievement_id === ach.id);
                const tierColors = { bronze: "#CD7F32", silver: "#C0C0C0", gold: "#FFD700" };
                const TierIcon = { bronze: Award, silver: Trophy, gold: Crown }[ach.tier] || Award;
                return (
                  <div key={ach.id} className={`p-4 rounded-lg mb-3 border ${unlocked ? "border-2" : ""}`} style={{ borderColor: unlocked ? tierColors[ach.tier] : "var(--border)", background: unlocked ? `${tierColors[ach.tier]}10` : "var(--background)" }} data-testid={`achievement-${ach.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: unlocked ? tierColors[ach.tier] : "var(--muted)", color: unlocked ? "white" : "var(--muted-foreground)" }}>
                        <TierIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold flex items-center gap-2">
                          {ach.name}
                          {unlocked && <CheckCircle className="w-4 h-4" style={{ color: tierColors[ach.tier] }} />}
                        </div>
                        <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>{ach.description}</div>
                        <div className="text-xs mt-1" style={{ color: "var(--accent)" }}>Reward: {ach.reward_value} Gift Card</div>

                        {unlocked && (
                          <div className="mt-3 p-3 rounded-lg" style={{ background: "var(--secondary)" }}>
                            <div className="flex items-center gap-2 mb-1"><Gift className="w-4 h-4" style={{ color: tierColors[ach.tier] }} /><span className="text-sm font-semibold">Your Coupon</span></div>
                            <div className="flex items-center gap-2">
                              <code className="font-mono text-sm font-bold px-2 py-1 rounded" style={{ background: "var(--background)", border: "1px dashed var(--border)" }} data-testid={`coupon-${ach.id}`}>{unlocked.coupon_code}</code>
                              <button onClick={() => copyCoupon(unlocked.coupon_code)} className="p-1.5 rounded hover:bg-[var(--muted)]" data-testid={`copy-coupon-${ach.id}`}>
                                {copiedCoupon === unlocked.coupon_code ? <CheckCircle className="w-4 h-4" style={{ color: "var(--primary)" }} /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                            <div className="text-xs mt-1" style={{ color: "var(--muted-foreground)" }}>{unlocked.coupon_description}</div>
                          </div>
                        )}

                        {!unlocked && (
                          <div className="mt-2">
                            <div className="w-full h-2 rounded-full" style={{ background: "var(--muted)" }}>
                              <div className="h-full rounded-full transition-all" style={{ background: tierColors[ach.tier], width: `${Math.min(100, (ach.type === "streak" ? achievementsData.focus_streak : achievementsData.focus_sessions) / ach.threshold * 100)}%` }} />
                            </div>
                            <div className="text-xs mt-1" style={{ color: "var(--muted-foreground)" }}>
                              {ach.type === "streak" ? achievementsData.focus_streak : achievementsData.focus_sessions}/{ach.threshold}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Browse Marketplace Button */}
              <button className="btn-accent w-full mt-4 flex items-center justify-center gap-2" onClick={() => { setShowAchievements(false); fetchMarketplace(); setShowMarketplace(true); }} data-testid="browse-marketplace-button">
                <ShoppingBag className="w-5 h-5" /> Browse Reward Marketplace
              </button>
            </motion.div>
          </motion.div>
        )}

        {/* Marketplace Modal */}
        {showMarketplace && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowMarketplace(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} style={{ maxHeight: "90vh" }} data-testid="marketplace-modal">
              <div className="flex justify-between items-center mb-2">
                <h2 className="section-title">Reward Marketplace</h2>
                <button onClick={() => setShowMarketplace(false)} data-testid="close-marketplace-modal"><X className="w-6 h-6" /></button>
              </div>

              {/* Upgrade Banner for Free users */}
              {!isPro && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} style={{ background: 'linear-gradient(135deg, #2c6b3f 0%, #1a4a2e 100%)', borderRadius: 16, padding: '20px', marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                  <div style={{ position: 'absolute', bottom: -30, right: 20, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
                  <div className="flex items-start gap-3" style={{ position: 'relative' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Crown className="w-6 h-6" style={{ color: '#FFD700' }} />
                    </div>
                    <div className="flex-1">
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Pro-Abo freischalten</div>
                      <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
                        Verdiene Coupons (Bronze, Silber, Gold) durch Focus-Achievements und löse sie hier gegen Geschenkkarten ein — ab €0 Eigenkosten.
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                        {['Geschenkkarten einlösen', '30-Tage Streak Bonus', 'Unbegrenzte Apps'].map(f => (
                          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                            <CheckCircle className="w-3 h-3" style={{ color: '#4ade80' }} />{f}
                          </div>
                        ))}
                      </div>
                      <button onClick={() => { setShowMarketplace(false); setShowPlans(true); }} data-testid="marketplace-upgrade-btn" style={{ background: '#FFD700', color: '#1a1a1a', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Sparkles className="w-4 h-4" /> Jetzt upgraden — ab $9.99/Monat
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Daily Login Streak Reward Section */}
              <GiftCardRewardSection user={user} api={api} />

              <p className="text-sm mb-4 mt-4" style={{ color: "var(--muted-foreground)" }}>
                {marketplaceData.available_coupons.length > 0
                  ? `You have ${marketplaceData.available_coupons.length} coupon${marketplaceData.available_coupons.length > 1 ? "s" : ""} to redeem!`
                  : "Earn coupons by completing focus achievements!"
                }
              </p>

              {/* Available Coupons Banner */}
              {marketplaceData.available_coupons.length > 0 && (
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {marketplaceData.available_coupons.map(c => {
                    const tierColors = { bronze: "#CD7F32", silver: "#C0C0C0", gold: "#FFD700" };
                    return (
                      <div key={c.coupon_code} className="flex-shrink-0 px-3 py-2 rounded-lg border-2 flex items-center gap-2" style={{ borderColor: tierColors[c.tier], background: `${tierColors[c.tier]}15` }} data-testid={`available-coupon-${c.tier}`}>
                        <Ticket className="w-4 h-4" style={{ color: tierColors[c.tier] }} />
                        <div>
                          <div className="text-xs font-bold" style={{ color: tierColors[c.tier] }}>{c.coupon_discount}</div>
                          <code className="text-xs font-mono">{c.coupon_code}</code>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Category Filter */}
              <div className="flex gap-2 mb-4">
                {[{id: "all", label: "All", icon: Package}, {id: "gift_card", label: "Gift Cards", icon: Gift}, {id: "game_card", label: "Game Cards", icon: Gamepad2}].map(cat => (
                  <button key={cat.id} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-all ${marketplaceFilter === cat.id ? "text-white" : ""}`} style={{ background: marketplaceFilter === cat.id ? "var(--primary)" : "var(--secondary)", color: marketplaceFilter === cat.id ? "var(--primary-foreground)" : "var(--foreground)" }} onClick={() => setMarketplaceFilter(cat.id)} data-testid={`filter-${cat.id}`}>
                    <cat.icon className="w-4 h-4" />{cat.label}
                  </button>
                ))}
              </div>

              {/* Items Grid */}
              <div className="space-y-3" style={{ maxHeight: "50vh", overflowY: "auto" }}>
                {filteredMarketplaceItems.map(item => {
                  const ITEM_ICONS = { "shopping-bag": ShoppingBag, "coffee": Coffee, "music": Music, "credit-card": CreditCard, "gamepad-2": Gamepad2 };
                  const ItemIcon = ITEM_ICONS[item.icon] || Gift;
                  const tierColors = { bronze: "#CD7F32", silver: "#C0C0C0", gold: "#FFD700" };
                  return (
                    <motion.div key={item.id} className="p-4 rounded-xl border" style={{ borderColor: item.can_redeem ? "var(--primary)" : "var(--border)", background: "var(--background)" }} whileHover={{ scale: 1.01 }} data-testid={`marketplace-item-${item.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: item.color, color: "white" }}>
                          <ItemIcon className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{item.name}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: "var(--secondary)", color: "var(--muted-foreground)" }}>{item.value}</span>
                          </div>
                          <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>{item.brand}</div>
                          <div className="text-xs mt-1" style={{ color: "var(--muted-foreground)" }}>{item.description}</div>

                          <div className="flex items-center gap-2 mt-2">
                            {item.can_redeem ? (
                              <>
                                <span className="text-sm line-through" style={{ color: "var(--muted-foreground)" }}>${item.original_price.toFixed(2)}</span>
                                <span className="text-sm font-bold" style={{ color: "var(--primary)" }}>${item.discounted_price.toFixed(2)}</span>
                                <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ background: `${tierColors[item.applicable_tier]}20`, color: tierColors[item.applicable_tier] }}>-{item.discount_percent}%</span>
                              </>
                            ) : (
                              <span className="text-sm font-semibold">${item.original_price.toFixed(2)}</span>
                            )}
                          </div>

                          {item.can_redeem ? (
                            <button className="btn-primary w-full mt-3 py-2 text-sm flex items-center justify-center gap-1.5" onClick={() => redeemCoupon(item.id, item.applicable_coupon)} disabled={redeeming === item.id} data-testid={`redeem-${item.id}`}>
                              {redeeming === item.id ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <><Tag className="w-4 h-4" /> Redeem with {item.applicable_tier} coupon</>}
                            </button>
                          ) : (
                            <div className="mt-3 py-2 px-3 rounded-lg text-xs text-center" style={{ background: "var(--muted)", color: "var(--muted-foreground)" }}>
                              Requires {item.min_tier} tier coupon or higher
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Past Redemptions */}
              {marketplaceData.redemptions.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold mb-3 flex items-center gap-2"><Package className="w-4 h-4" /> Your Redemptions</h3>
                  {marketplaceData.redemptions.map(r => (
                    <div key={r.id} className="p-3 rounded-lg mb-2 flex items-center justify-between" style={{ background: "var(--secondary)" }} data-testid={`redemption-${r.id}`}>
                      <div>
                        <div className="font-medium text-sm">{r.item_name} ({r.item_value})</div>
                        <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>{r.item_brand} - Saved {r.discount_percent}%</div>
                      </div>
                      <div className="text-right">
                        <code className="text-xs font-mono font-bold" style={{ color: "var(--primary)" }}>{r.redemption_code}</code>
                        <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>{new Date(r.redeemed_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* Redemption Success Modal */}
        {showRedemptionSuccess && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowRedemptionSuccess(null)} style={{ zIndex: 10000 }}>
            <motion.div className="modal-content" initial={{ scale: 0.9, y: "50%" }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }} onClick={e => e.stopPropagation()} style={{ borderRadius: "1.5rem" }} data-testid="redemption-success-modal">
              <div className="text-center py-4">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.1 }} className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--primary), var(--accent))", color: "white" }}>
                  <Gift className="w-10 h-10" />
                </motion.div>
                <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "Manrope" }}>Reward Redeemed!</h2>
                <p className="text-sm mb-4" style={{ color: "var(--muted-foreground)" }}>{showRedemptionSuccess.message}</p>

                <div className="p-4 rounded-xl mb-4" style={{ background: "var(--secondary)" }}>
                  <div className="text-xs mb-1 font-medium" style={{ color: "var(--muted-foreground)" }}>Your Redemption Code</div>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-lg font-mono font-bold px-3 py-2 rounded-lg" style={{ background: "var(--background)", border: "2px dashed var(--primary)", color: "var(--primary)" }} data-testid="final-redemption-code">{showRedemptionSuccess.redemption_code}</code>
                    <button onClick={() => copyCoupon(showRedemptionSuccess.redemption_code)} className="p-2 rounded-lg hover:bg-[var(--muted)]" data-testid="copy-redemption-code">
                      {copiedCoupon === showRedemptionSuccess.redemption_code ? <CheckCircle className="w-5 h-5" style={{ color: "var(--primary)" }} /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="text-xs mt-2" style={{ color: "var(--muted-foreground)" }}>
                    {showRedemptionSuccess.item_brand} {showRedemptionSuccess.item_name} ({showRedemptionSuccess.item_value}) for ${showRedemptionSuccess.discounted_price?.toFixed(2)}
                  </div>
                </div>

                <button className="btn-primary w-full" onClick={() => setShowRedemptionSuccess(null)} data-testid="close-redemption-success">Continue Shopping</button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Referral Modal */}
        {showReferral && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowReferral(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} data-testid="referral-modal">
              <div className="flex justify-between items-center mb-6"><h2 className="section-title">Refer a Friend</h2><button onClick={() => setShowReferral(false)} data-testid="close-referral-modal"><X className="w-6 h-6" /></button></div>

              <div className="p-4 rounded-xl mb-5" style={{ background: "linear-gradient(135deg, var(--primary), #3D6B52)", color: "white" }}>
                <div className="text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-90" />
                  <div className="font-bold text-lg mb-1" style={{ fontFamily: "Manrope" }}>Share & Earn</div>
                  <div className="text-sm opacity-90 mb-4">Invite friends to Nova. Both of you earn a Bronze coupon (10% OFF)!</div>
                  {referralData && (
                    <div className="flex items-center justify-center gap-2">
                      <code className="text-lg font-mono font-bold px-4 py-2 rounded-lg" style={{ background: "rgba(255,255,255,0.2)", border: "1px dashed rgba(255,255,255,0.4)" }} data-testid="referral-code">{referralData.code}</code>
                      <button onClick={() => copyCoupon(referralData.code)} className="p-2 rounded-lg" style={{ background: "rgba(255,255,255,0.2)" }} data-testid="copy-referral-code">
                        {copiedCoupon === referralData.code ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {referralData && referralData.referred_count > 0 && (
                <div className="p-3 rounded-lg mb-4 flex items-center gap-3" style={{ background: "var(--secondary)" }}>
                  <Trophy className="w-5 h-5" style={{ color: "var(--accent)" }} />
                  <div className="text-sm"><span className="font-bold">{referralData.referred_count}</span> friend{referralData.referred_count > 1 ? "s" : ""} referred</div>
                </div>
              )}

              <div className="mt-4">
                <div className="font-semibold text-sm mb-2">Have a referral code?</div>
                {referralMsg && (
                  <div className={`p-3 rounded-lg mb-3 text-sm ${referralMsg.type === "success" ? "" : ""}`} style={{ background: referralMsg.type === "success" ? "rgba(44,76,59,0.1)" : "rgba(196,109,94,0.1)", color: referralMsg.type === "success" ? "var(--primary)" : "var(--accent)" }} data-testid="referral-message">{referralMsg.text}</div>
                )}
                <div className="flex gap-2">
                  <input type="text" className="ai-input flex-1" placeholder="Enter referral code" value={referralInput} onChange={e => setReferralInput(e.target.value)} data-testid="referral-input" />
                  <button className="btn-primary" onClick={applyReferral} data-testid="apply-referral-button">Apply</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Schedule Modal */}
        {showSchedule && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowSchedule(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} data-testid="schedule-modal">
              <div className="flex justify-between items-center mb-6"><h2 className="section-title">Focus Schedule</h2><button onClick={() => setShowSchedule(false)} data-testid="close-schedule-modal"><X className="w-6 h-6" /></button></div>

              {/* Existing Schedules */}
              {schedules.length > 0 && (
                <div className="mb-6">
                  {schedules.map(s => (
                    <div key={s.id} className="p-3 rounded-lg mb-2 flex items-center justify-between" style={{ background: "var(--secondary)", opacity: s.active ? 1 : 0.5 }} data-testid={`schedule-${s.id}`}>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">{s.name}</div>
                        <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                          {s.days.map(d => d.slice(0, 3).toUpperCase()).join(", ")} at {s.start_time} ({s.duration_minutes}min)
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleScheduleActive(s.id)} className="p-1.5 rounded hover:bg-[var(--muted)]" data-testid={`toggle-schedule-${s.id}`}>
                          {s.active ? <ToggleRight className="w-5 h-5" style={{ color: "var(--primary)" }} /> : <ToggleLeft className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />}
                        </button>
                        <button onClick={() => deleteSchedule(s.id)} className="p-1.5 rounded hover:bg-[var(--muted)]" data-testid={`delete-schedule-${s.id}`}>
                          <Trash2 className="w-4 h-4" style={{ color: "var(--accent)" }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* New Schedule Form */}
              <div className="p-4 rounded-xl" style={{ border: "1px solid var(--border)" }}>
                <div className="font-semibold text-sm mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> New Schedule</div>
                <input type="text" className="ai-input w-full mb-3" placeholder="Schedule name (e.g. Morning Focus)" value={newSchedule.name} onChange={e => setNewSchedule(prev => ({ ...prev, name: e.target.value }))} data-testid="schedule-name-input" />

                <div className="text-xs font-medium mb-2" style={{ color: "var(--muted-foreground)" }}>Days</div>
                <div className="flex gap-1.5 mb-3 flex-wrap">
                  {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map(day => (
                    <button key={day} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all" style={{ background: newSchedule.days.includes(day) ? "var(--primary)" : "var(--muted)", color: newSchedule.days.includes(day) ? "white" : "var(--foreground)" }} onClick={() => toggleDay(day)} data-testid={`day-${day}`}>
                      {day.toUpperCase()}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <div className="text-xs font-medium mb-1" style={{ color: "var(--muted-foreground)" }}>Start Time</div>
                    <input type="time" className="ai-input w-full" value={newSchedule.start_time} onChange={e => setNewSchedule(prev => ({ ...prev, start_time: e.target.value }))} data-testid="schedule-time-input" />
                  </div>
                  <div>
                    <div className="text-xs font-medium mb-1" style={{ color: "var(--muted-foreground)" }}>Duration (min)</div>
                    <input type="number" className="ai-input w-full" min={15} max={240} value={newSchedule.duration_minutes} onChange={e => setNewSchedule(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))} data-testid="schedule-duration-input" />
                  </div>
                </div>

                <button className="btn-primary w-full flex items-center justify-center gap-2" onClick={createSchedule} disabled={!newSchedule.name.trim() || newSchedule.days.length === 0} data-testid="create-schedule-button">
                  <Plus className="w-4 h-4" /> Create Schedule
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* AI Chat Modal */}
        {showAI && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAI(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} style={{ height: "85vh" }} data-testid="ai-modal">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3"><img src={NOVA_AVATAR} alt="Nova" className="ai-avatar" /><div><h2 className="font-semibold">Nova</h2><div className="text-xs" style={{ color: "var(--muted-foreground)" }}>{isPro && user?.ai_mode === "gpt" ? "GPT-5.2 Mode" : "Assistant"}</div></div></div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setTtsEnabled(!ttsEnabled)} className="p-2 rounded-full hover:bg-[var(--muted)]" data-testid="tts-toggle">{ttsEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}</button>
                  <button onClick={() => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; } setShowAI(false); }} data-testid="close-ai-modal"><X className="w-6 h-6" /></button>
                </div>
              </div>
              {isPro && (
                <div className="ai-mode-toggle">
                  <button className={`ai-mode-btn ${user?.ai_mode === "rule_based" ? "active" : ""}`} onClick={() => updateSettings({ ai_mode: "rule_based" })}>Rule-Based</button>
                  <button className={`ai-mode-btn ${user?.ai_mode === "gpt" ? "active" : ""}`} onClick={() => updateSettings({ ai_mode: "gpt" })}>GPT-5.2 <Zap className="inline w-3 h-3 ml-1" /></button>
                </div>
              )}
              <div className="ai-chat-container">
                <div className="ai-messages">
                  {messages.map(m => (
                    <div key={m.id} className={`ai-message ${m.role}`}>
                      {m.role === "ai" && <img src={NOVA_AVATAR} alt="Nova" className="ai-avatar" />}
                      <div className={`message-bubble ${m.role}`}>{m.text}</div>
                      {m.role === "user" && <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--primary)" }}><User className="w-5 h-5 text-white" /></div>}
                    </div>
                  ))}
                  {isSending && <div className="ai-message ai"><img src={NOVA_AVATAR} alt="Nova" className="ai-avatar" /><div className="message-bubble ai"><div className="spinner" /></div></div>}
                  {isSpeaking && <div className="flex items-center gap-2 text-sm" style={{ color: "var(--muted-foreground)" }}><div className="speaking-indicator"><div className="speaking-dot" /><div className="speaking-dot" /><div className="speaking-dot" /></div>Nova is speaking...</div>}
                  <div ref={messagesEndRef} />
                </div>
                <div className="ai-input-container">
                  <input type="text" className="ai-input" placeholder="Ask Nova anything..." value={userInput} onChange={e => setUserInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSend()} disabled={isSending} data-testid="ai-input" />
                  <button className="send-btn" onClick={handleSend} disabled={isSending || !userInput.trim()} data-testid="ai-send-button"><Send className="w-5 h-5" /></button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* App Edit Modal */}
        {showAppEdit && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowAppEdit(null)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} data-testid="app-edit-modal">
              <div className="flex justify-between items-center mb-6"><h2 className="section-title">Edit {showAppEdit.name}</h2><button onClick={() => setShowAppEdit(null)} data-testid="close-app-edit-modal"><X className="w-6 h-6" /></button></div>
              <div className="mb-4"><label className="block text-sm font-medium mb-2">Daily Time Limit (minutes)</label><input type="number" className="ai-input w-full" defaultValue={showAppEdit.minutes_allowed} min={1} max={180} id="app-minutes-input" data-testid="app-minutes-input" /></div>
              <button className="btn-primary w-full" onClick={() => { const v = document.getElementById("app-minutes-input").value; updateApps(apps.map(a => a.id === showAppEdit.id ? { ...a, minutes_allowed: parseInt(v) || 30 } : a)); setShowAppEdit(null); }} data-testid="save-app-minutes">Save Changes</button>
            </motion.div>
          </motion.div>
        )}

        {/* Customize Modal */}
        {showCustomize && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCustomize(false)}>
            <motion.div className="modal-content" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} onClick={e => e.stopPropagation()} data-testid="customize-modal">
              <div className="flex justify-between items-center mb-6"><h2 className="section-title">Customize</h2><button onClick={() => setShowCustomize(false)} data-testid="close-customize-modal"><X className="w-6 h-6" /></button></div>
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Theme</h3>
                <div className="selector-grid">
                  {constants.themes.map(t => (
                    <div key={t.id} className={`selector-item ${user?.selected_theme === t.id ? "selected" : ""}`} style={{ background: t.preview }} onClick={() => updateSettings({ selected_theme: t.id })} data-testid={`theme-${t.id}`}>
                      <span className="text-sm font-medium text-white drop-shadow">{t.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Avatar</h3>
                <div className="selector-grid">
                  {constants.avatars.map(a => (
                    <div key={a.id} className={`selector-item ${user?.selected_avatar === a.id ? "selected" : ""}`} style={{ background: "var(--secondary)" }} onClick={() => updateSettings({ selected_avatar: a.id })} data-testid={`avatar-${a.id}`}>
                      <div className="text-center"><div className="text-3xl mb-1">{AVATAR_ICONS[a.id]}</div><div className="text-sm">{a.name}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ ROUTER ============
// ============ LANGUAGE SWITCHER ============
function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];
  const [open, setOpen] = useState(false);

  return (
    <div className="clay-card p-4 mb-4" data-testid="language-section" style={{ position: 'relative' }}>
      <div className="flex justify-between items-center cursor-pointer" onClick={() => setOpen(!open)}>
        <div>
          <div className="font-semibold text-sm">Language / Sprache</div>
          <div className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{current.flag} {current.label}</div>
        </div>
        <Globe className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
      </div>
      {open && (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {LANGUAGES.map(lang => (
            <button key={lang.code} onClick={() => { i18n.changeLanguage(lang.code); setOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, border: '1.5px solid', borderColor: i18n.language === lang.code ? 'var(--primary)' : 'var(--border)', background: i18n.language === lang.code ? 'var(--primary)' : 'transparent', color: i18n.language === lang.code ? 'var(--primary-foreground)' : 'var(--foreground)', cursor: 'pointer', fontSize: 13, fontWeight: i18n.language === lang.code ? 700 : 400 }}>
              <span style={{ fontSize: 16 }}>{lang.flag}</span>{lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ SPOTIFY CALLBACK ============
function SpotifyCallback() {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');

    if (error || !code) {
      setStatus('error');
      setTimeout(() => { try { window.close(); } catch(e) { /* window.close() can fail in some browsers */ } }, 2000);
      return;
    }

    const verifier = localStorage.getItem('spotify_pkce_verifier');
    if (!verifier) { setStatus('error'); return; }

    // Exchange code for token (PKCE — no client secret needed)
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: window.location.origin + '/spotify-cb',
      client_id: '5371814e58f54100b2b55084b641665f',
      code_verifier: verifier,
    });

    fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
      .then(r => r.json())
      .then(data => {
        if (data.access_token) {
          localStorage.setItem('spotify_token', data.access_token);
          localStorage.setItem('spotify_token_time', Date.now());
          localStorage.removeItem('spotify_pkce_verifier');
          setStatus('success');
          setTimeout(() => { try { window.close(); } catch(e) { /* window.close() can fail in some browsers */ } }, 1000);
        } else {
          setStatus('error');
        }
      })
      .catch(e => { console.error('Spotify token exchange failed:', e); setStatus('error'); });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#191414', color: '#1DB954', fontFamily: 'sans-serif' }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="#1DB954" style={{ marginBottom: 16 }}><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
      {status === 'loading' && <p style={{ fontSize: 16 }}>Verbinde Spotify...</p>}
      {status === 'success' && <><p style={{ fontSize: 16 }}>Spotify verbunden!</p><p style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>Dieses Fenster schließt sich...</p></>}
      {status === 'error' && <p style={{ fontSize: 16, color: '#f44' }}>Verbindung fehlgeschlagen</p>}
    </div>
  );
}

// ============ DATENSCHUTZ ============
function PrivacyPolicy() {
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px', fontFamily: 'system-ui, sans-serif', color: '#1a1a1a', lineHeight: 1.7 }}>
      <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#2c6b3f', textDecoration: 'none', fontSize: 14, marginBottom: 32 }}>
        ← Zurück zur App
      </a>
      <h1 style={{ fontSize: 32, fontWeight: 800, marginBottom: 8 }}>Datenschutzerklärung</h1>
      <p style={{ color: '#666', marginBottom: 40 }}>Zuletzt aktualisiert: Oktober 2025 · Nova Focus Assistant · novassist.online</p>

      <Section title="1. Verantwortliche Stelle">
        <p>Verantwortlich für die Verarbeitung personenbezogener Daten im Sinne der DSGVO ist:</p>
        <p><strong>Nova Focus Assistant</strong><br />
        Betrieben unter: novassist.online<br />
        Kontakt: <a href="mailto:support@novassist.online" style={{ color: '#2c6b3f' }}>support@novassist.online</a></p>
      </Section>

      <Section title="2. Welche Daten wir erheben">
        <p>Wir erheben und verarbeiten folgende personenbezogene Daten:</p>
        <ul>
          <li><strong>Kontodaten:</strong> Name, E-Mail-Adresse, verschlüsseltes Passwort</li>
          <li><strong>Nutzungsdaten:</strong> Focus-Sessions, Focus-Minuten, Login-Streak, Achievements</li>
          <li><strong>Gerätedaten:</strong> Betriebssystem, App-Version (keine Geräte-IDs)</li>
          <li><strong>Zahlungsdaten:</strong> Werden ausschließlich von Stripe verarbeitet – wir speichern keine Kreditkartendaten</li>
          <li><strong>Spotify-Daten:</strong> Nur bei freiwilliger Verbindung: zuletzt gespielte Tracks während Focus-Sessions</li>
        </ul>
      </Section>

      <Section title="3. Zweck der Datenverarbeitung">
        <ul>
          <li>Bereitstellung und Betrieb der Nova Focus Assistant App</li>
          <li>Authentifizierung und Kontoverwaltung</li>
          <li>Verarbeitung von Zahlungen (Pro/Family-Abonnements)</li>
          <li>Gamification-Features (Achievements, Streaks, Rewards)</li>
          <li>KI-basierter Chat-Assistent (Anfragen werden nicht dauerhaft gespeichert)</li>
          <li>Verbesserung der App-Funktionalität</li>
        </ul>
      </Section>

      <Section title="4. Rechtsgrundlage">
        <p>Die Verarbeitung erfolgt auf Basis von:</p>
        <ul>
          <li><strong>Art. 6 Abs. 1 lit. b DSGVO</strong> – Vertragserfüllung (Nutzerkonto, Abonnement)</li>
          <li><strong>Art. 6 Abs. 1 lit. a DSGVO</strong> – Einwilligung (Spotify-Verbindung, optionale Features)</li>
          <li><strong>Art. 6 Abs. 1 lit. f DSGVO</strong> – Berechtigtes Interesse (App-Sicherheit, Missbrauchsprävention)</li>
        </ul>
      </Section>

      <Section title="5. Weitergabe an Dritte">
        <p>Wir geben Daten nur an folgende Drittanbieter weiter, soweit dies für den Betrieb notwendig ist:</p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Anbieter</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Zweck</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Datenschutz</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Stripe Inc.', 'Zahlungsabwicklung', 'stripe.com/privacy'],
              ['Reloadly', 'Digitale Geschenkkarten', 'reloadly.com/privacy'],
              ['Spotify AB', 'Musik-Tracking (optional)', 'spotify.com/privacy'],
              ['OpenAI', 'KI-Chat-Assistent', 'openai.com/privacy'],
              ['MongoDB Atlas', 'Datenbankhosting (EU)', 'mongodb.com/privacy'],
            ].map(([name, purpose, url]) => (
              <tr key={name} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '10px 12px', fontWeight: 500 }}>{name}</td>
                <td style={{ padding: '10px 12px', color: '#555' }}>{purpose}</td>
                <td style={{ padding: '10px 12px' }}><a href={`https://${url}`} style={{ color: '#2c6b3f', fontSize: 12 }} target="_blank" rel="noreferrer">{url}</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="6. Datenspeicherung & Löschung">
        <p>Deine Daten werden gespeichert, solange dein Konto aktiv ist. Bei Kontolöschung werden alle personenbezogenen Daten innerhalb von <strong>30 Tagen</strong> endgültig gelöscht. Ausnahmen gelten für gesetzliche Aufbewahrungsfristen (z. B. Rechnungsdaten: 7 Jahre).</p>
      </Section>

      <Section title="7. Deine Rechte (DSGVO)">
        <ul>
          <li><strong>Auskunft</strong> (Art. 15): Du kannst jederzeit Auskunft über deine gespeicherten Daten anfordern</li>
          <li><strong>Berichtigung</strong> (Art. 16): Unrichtige Daten können korrigiert werden</li>
          <li><strong>Löschung</strong> (Art. 17): Du kannst die Löschung deines Kontos und aller Daten verlangen</li>
          <li><strong>Einschränkung</strong> (Art. 18): Du kannst die Verarbeitung einschränken lassen</li>
          <li><strong>Datenübertragbarkeit</strong> (Art. 20): Export deiner Daten auf Anfrage</li>
          <li><strong>Widerspruch</strong> (Art. 21): Widerspruch gegen die Verarbeitung</li>
          <li><strong>Beschwerde</strong>: Bei der zuständigen Datenschutzbehörde</li>
        </ul>
        <p>Anfragen bitte an: <a href="mailto:support@novassist.online" style={{ color: '#2c6b3f' }}>support@novassist.online</a></p>
      </Section>

      <Section title="8. Sicherheit">
        <p>Alle Daten werden verschlüsselt übertragen (HTTPS/TLS). Passwörter werden mit bcrypt gehasht gespeichert. Zahlungsdaten verlassen nie unsere Server und werden direkt von Stripe verarbeitet.</p>
      </Section>

      <Section title="9. Minderjährige">
        <p>Nova Focus Assistant richtet sich nicht an Kinder unter 13 Jahren. Wir erheben wissentlich keine Daten von Minderjährigen unter 13 Jahren.</p>
      </Section>

      <Section title="10. Änderungen dieser Erklärung">
        <p>Wir behalten uns vor, diese Datenschutzerklärung bei Bedarf anzupassen. Die aktuelle Version ist stets unter <a href="https://novassist.online/datenschutz" style={{ color: '#2c6b3f' }}>novassist.online/datenschutz</a> verfügbar. Bei wesentlichen Änderungen werden Nutzer per E-Mail informiert.</p>
      </Section>

      <div style={{ marginTop: 48, padding: 24, background: '#f9f9f9', borderRadius: 12, fontSize: 14, color: '#666' }}>
        <strong>Kontakt für Datenschutzanfragen:</strong><br />
        Nova Focus Assistant · <a href="mailto:support@novassist.online" style={{ color: '#2c6b3f' }}>support@novassist.online</a> · novassist.online
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12, color: '#1a1a1a', borderBottom: '2px solid #2c6b3f', paddingBottom: 8 }}>{title}</h2>
      {children}
    </div>
  );
}

function AppRouter() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Check for Google OAuth callback
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  // Spotify OAuth callback (popup window)
  if (location.pathname === '/spotify-cb') return <SpotifyCallback />;

  // Datenschutz — öffentlich, kein Login nötig
  if (location.pathname === '/datenschutz') return <PrivacyPolicy />;

  // Admin Panel — komplett eigenständig
  if (location.pathname.startsWith('/admin')) return <AdminPanel />;

  if (loading) return <div className="auth-page"><div className="spinner" style={{ margin: "auto" }} /></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/" /> : <RegisterPage />} />
      <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/login" />} />
      <Route path="*" element={user ? <MainApp /> : <Navigate to="/login" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppRouter />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
